/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useState } from 'react'
import { styled, useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import Tooltip, { type TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import type { ChatListT, ReactionT, FeedbackT } from '../types'
import { sendResponseReaction } from '../../../../utils/api'
import FeedbackWidget from './Feedback/index'
import CustomSnackBar from '../../../CustomConversationSnackBar'
import { SnackBarMessageType } from '../../../../consts/SnackbarMessageTypes'
import {
  StyledBotMessage,
  StyledBotMessageWrapper,
  StyledFeedbackWrapper,
  StyledReactionWrapper,
  StyledTimeStamp,
  StyledIconButton,
  StyledMarkdownSection
} from './styled'
import getOrientation from '../../../../utils/getOrientation'
import getForcedMobile from '../../../../utils/forceMobile'
import { useAppSelector } from '../../../../reducers/hooks'
import { formatBotMessage } from '../../../../utils/formatBotMessage'
import { FaRegCopy, FaRegThumbsDown, FaRegThumbsUp, FaRepeat, FaThumbsDown, FaThumbsUp } from 'react-icons/fa6'

interface BotMessageProps {
  message: ChatListT
  chatRecordId?: string
  isFeedbackSubmit: (val: boolean) => void
  handleRegenerate: (val: string) => Promise<any>
  userMessage: ChatListT
  handleRead: () => void
}

export const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: 'black',
    left: '-36px !important'
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    left: '36px !important',
    borderRadius: '4px',
    fontSize: '16px',
    padding: '12px 8px',
    width: 'fit-content',
    height: '42px'
  }
}))

const BotMessage: React.FC<BotMessageProps> = ({ message, userMessage, chatRecordId, isFeedbackSubmit, handleRegenerate, handleRead }) => {
  const theme = useTheme()
  const isPortrait = getOrientation()
  const forcedMobile = getForcedMobile()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const platform = useAppSelector((state) => state.user.platform)
  const botType = useAppSelector((state) => state.user.botType)
  const isRegenerated = userMessage?.regeneratedQuestion === true
  const [messageRead] = useState<boolean>(message?.read)
  const [showThumbsUp, setShowThumbsUp] = useState<boolean>(message?.binaryFeedback === true)
  const [showThumbsDown, setShowThumbsDown] = useState<boolean>(message?.binaryFeedback === false)
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(isRegenerated)
  const [showfeedbackSnackbar, setShowFeedbackSnackbar] = useState<boolean>(false)
  const [showCopySnackbar, setShowCopySnackbar] = useState<boolean>(false)
  const [showCopySnackbarError, setShowCopySnackbarError] = useState<boolean>(false)
  const [showRegeneratedFeedback, setShowRegeneratedFeedback] = useState<boolean>(isRegenerated)

  const SubmitReaction = async (chatRecordId: string | undefined, feedback: ReactionT | FeedbackT): Promise<void> => {
    try {
      await sendResponseReaction(chatRecordId, feedback, platform, botType).then((res) => {
      })
    } catch (err: any) {
      console.log('Err', err)
    }
  }

  const handleReaction = async (reaction: string): Promise<void> => {
    if (reaction === 'up') {
      setShowThumbsUp(true)
      setShowThumbsDown(false)
      isFeedbackSubmit(true)
      await SubmitReaction(chatRecordId, { rating: true })
      setShowFeedbackModal(false)
    } else if (reaction === 'down') {
      setShowThumbsUp(false)
      setShowThumbsDown(true)
      isFeedbackSubmit(true)
      await SubmitReaction(chatRecordId, { rating: false })
      setShowFeedbackModal(true)
    } else {
      // Do nothing
    }
  }

  const handleOnClose = (): void => {
    setShowFeedbackModal(false)
  }

  const handleOnSubmit = async (text: string): Promise<void> => {
    if (isRegenerated) {
      if (text === 'Better') {
        setShowThumbsUp(true)
        isFeedbackSubmit(true)
        await SubmitReaction(chatRecordId, { rating: true })
        await SubmitReaction(chatRecordId, { message: 'Better' })
      } else if (text === 'Worse') {
        setShowThumbsDown(true)
        isFeedbackSubmit(true)
        await SubmitReaction(chatRecordId, { rating: false })
        await SubmitReaction(chatRecordId, { message: 'Worse' })
      } else if (text === 'Same') {
        isFeedbackSubmit(true)
        await SubmitReaction(chatRecordId, { message: 'Same' })
      } else {
        // Do nothing
      }
      setShowRegeneratedFeedback(false)
    }
    await SubmitReaction(chatRecordId, { message: text }).then(() => {
      setShowFeedbackSnackbar(true)
    })
  }

  const handleOnCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(message.text)
      setShowCopySnackbar(true)
    } catch (err: any) {
      console.log(err)
      setShowCopySnackbarError(true)
    }
  }

  useEffect(() => {
    if (messageRead) {
      handleRead?.()
    }
  }, [messageRead])

  useEffect(() => {
    if (message.binaryFeedback !== null && message.binaryFeedback !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
      if (message.binaryFeedback === true) {
        setShowThumbsUp(true)
        setShowThumbsDown(false)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
      } else if (message.binaryFeedback === false) {
        setShowThumbsUp(false)
        setShowThumbsDown(true)
      }
    } else {
      setShowThumbsUp(false)
      setShowThumbsDown(false)
    }
  }, [message.binaryFeedback])

  return (
    <>
      <StyledBotMessageWrapper key={chatRecordId} isMobile={isMobile || isPortrait || forcedMobile}>
        <StyledBotMessage>
          <StyledMarkdownSection>
            {formatBotMessage(message?.text)}
          </StyledMarkdownSection>
          {/* {!messageRead &&
          <StyledMarkdownSection>
            <Typewriter text={message?.text} speed={6} onComplete={() => { setMessageRead(true) }}/>
          </StyledMarkdownSection>
          } */}
          {chatRecordId !== undefined && (
            <StyledFeedbackWrapper>
              {!showThumbsUp &&
              <StyledIconButton size="small" title="Thumbs Up" disableRipple={true} onClick={() => { handleReaction('up').catch(console.error) }}>
                <FaRegThumbsUp size={'16px'} />
              </StyledIconButton>
              }
              {showThumbsUp &&
              <StyledIconButton size="small" title="Thumbs Up" disableRipple={true}>
                <FaThumbsUp size={'16px'} />
              </StyledIconButton>
              }
              {!showThumbsDown &&
              <StyledIconButton size="small" title="Thumbs Down" disableRipple={true} onClick={() => { handleReaction('down').catch(console.error) }}>
                <FaRegThumbsDown size={'16px'} />
              </StyledIconButton>
              }
              {showThumbsDown &&
              <StyledIconButton size="small" title="Thumbs Down" disableRipple={true}>
                <FaThumbsDown size={'16px'} />
              </StyledIconButton>
              }
              <StyledIconButton size="small" title="Copy" disableRipple={true} onClick={async () => { await handleOnCopy() }}>
                <FaRegCopy size={'16px'} />
              </StyledIconButton>
              <StyledIconButton size="small" title="Regenerate Answer" disableRipple={true} onClick={() => { handleRegenerate(userMessage.text).catch(console.error) }}>
                <FaRepeat size={'16px'} />
              </StyledIconButton>
              <CustomSnackBar isOpen={showCopySnackbar} onClose={setShowCopySnackbar} message={'Message Copied!'} duration={1500} messageType={SnackBarMessageType.SUCCESS} />
              <CustomSnackBar isOpen={showCopySnackbarError} onClose={setShowCopySnackbarError} message={'Something went wrong copying message.'} duration={1500} messageType={SnackBarMessageType.ERROR} />
            </StyledFeedbackWrapper>
          )}
          {
            showFeedbackModal && messageRead && (
              <FeedbackWidget regenerated={showRegeneratedFeedback} open={showFeedbackModal} onClose={handleOnClose} onSubmit={handleOnSubmit} />
            )
          }
        </StyledBotMessage>

        <StyledReactionWrapper>
          <StyledTimeStamp>
            {message.timeStamp}
            {showfeedbackSnackbar && (
              <>
                <CustomSnackBar isOpen={showfeedbackSnackbar} onClose={setShowFeedbackSnackbar} message={'Thank you! Your feedback was sent.'} duration={1500} messageType={SnackBarMessageType.SUCCESS} />
              </>
            )}
          </StyledTimeStamp>
        </StyledReactionWrapper>
      </StyledBotMessageWrapper >
    </>
  )
}

export default BotMessage
